import React, { useState, useEffect, useRef } from "react";

import { useQuery, useMutation } from "@apollo/client";
import { ME_OWNER } from "../../actions/queries";
import {
  UPDATE_OWNER,
  GET_IMG_URL,
  DELETE_OWNER,
} from "../../actions/mutations";
import { useNavigate, Link } from "react-router-dom";
import { HexColorPicker } from "react-colorful";
import NavbarAccount from "../Layout/Navbar_account";
import { FrontCard, BackCardExplore, BackCardWallet } from "../Cards";
import Cookies from "js-cookie";
import {
  faArrowLeft,
  faInfoCircle,
  faEye,
  faEyeSlash,
  faChevronDown,
  faChevronUp,
  faPlus,
  faTrash,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import editElem from "../../utils/editElem";
import { togglePassword } from "../../utils/togglePassword";
import PhoneInput from "../PhoneInput";
import { getCountryByDialCode, formatPhoneForMutation } from "../../utils/phoneCountries";

/**
 * Custom Dropdown Component (matches LocationSelector style)
 */
const CustomDropdown = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <div
        style={{
          padding: '0.75rem 1rem',
          border: '1px solid #ddd',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '45px',
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span style={{ color: value ? '#000' : '#999' }}>
          {value || placeholder}
        </span>
        <FontAwesomeIcon 
          icon={isOpen ? faChevronUp : faChevronDown}
          style={{ color: '#666' }}
        />
      </div>
      
      {isOpen && options.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            maxHeight: '250px',
            overflowY: 'auto',
            listStyle: 'none',
            padding: 0,
            margin: '4px 0 0 0',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          {options.map((option) => (
            <li
              key={option.value}
              onClick={() => handleSelect(option)}
              style={{
                padding: '0.75rem 1rem',
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: value === option.label ? '#f5f5f5' : 'white',
              }}
              onMouseEnter={(e) => {
                if (value !== option.label) e.target.style.backgroundColor = '#f9f9f9';
              }}
              onMouseLeave={(e) => {
                if (value !== option.label) e.target.style.backgroundColor = 'white';
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * SearchableAddressSelector Component
 * Allows users to select France, then a department, then search for a city
 */
const SearchableAddressSelector = ({ onAdd, onCancel }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedDepartmentCode, setSelectedDepartmentCode] = useState('');
  const [departments, setDepartments] = useState({});
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [showDepartmentSuggestions, setShowDepartmentSuggestions] = useState(false);
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState('');
  const [filteredCities, setFilteredCities] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [streetName, setStreetName] = useState('');
  const [postalCode, setPostalCode] = useState('');

  // Load departments/wilayas when Country is selected
  useEffect(() => {
    if (selectedCountry === 'France') {
      fetch('/data/France/departements.json')
        .then(res => res.json())
        .then(data => setDepartments(data))
        .catch(err => console.error('Error loading departments:', err));
    } else if (selectedCountry === 'Algérie') {
      fetch('/data/Algérie/wilayas.json')
        .then(res => res.json())
        .then(data => setDepartments(data))
        .catch(err => console.error('Error loading wilayas:', err));
    }
  }, [selectedCountry]);

  // Filter departments as user types (or show all if empty)
  useEffect(() => {
    if (Object.keys(departments).length > 0) {
      if (departmentSearch) {
        const searchLower = departmentSearch.toLowerCase();
        const matches = Object.entries(departments)
          .sort(([codeA], [codeB]) => codeA.localeCompare(codeB))
          .filter(([code, name]) => 
            code.toLowerCase().includes(searchLower) || 
            name.toLowerCase().includes(searchLower)
          )
          .map(([code, name]) => ({ code, name }));
        setFilteredDepartments(matches);
      } else {
        // Show all departments when no search
        const allDepartments = Object.entries(departments)
          .sort(([codeA], [codeB]) => codeA.localeCompare(codeB))
          .map(([code, name]) => ({ code, name }));
        setFilteredDepartments(allDepartments);
      }
    }
  }, [departmentSearch, departments]);

  // Load cities when department/wilaya changes
  useEffect(() => {
    if (selectedDepartmentCode) {
      const countryFolder = selectedCountry === 'Algérie' ? 'Algérie' : 'France';
      fetch(`/data/${countryFolder}/communes-${selectedDepartmentCode}.json`)
        .then(res => res.json())
        .then(data => {
          setCities(data);
          setCitySearch('');
          setFilteredCities([]);
          setSelectedCity('');
        })
        .catch(err => console.error('Error loading cities:', err));
    }
  }, [selectedDepartmentCode, selectedCountry]);

  // Filter cities as user types (or show first 10 if empty)
  useEffect(() => {
    if (cities.length > 0) {
      if (citySearch) {
        const searchLower = citySearch.toLowerCase();
        const matches = cities
          .filter(city => city.toLowerCase().includes(searchLower))
          .slice(0, 10);
        setFilteredCities(matches);
      } else {
        // display all cities when no search
        setFilteredCities(cities);
      }
    }
  }, [citySearch, cities]);

  const handleCountrySelect = (option) => {
    setSelectedCountry(option.label);
    setSelectedDepartment('');
    setSelectedDepartmentCode('');
    setDepartmentSearch('');
    setCitySearch('');
    setSelectedCity('');
  };

  const handleDepartmentSelect = (dept) => {
    setSelectedDepartment(`${dept.code} - ${dept.name}`);
    setSelectedDepartmentCode(dept.code);
    setDepartmentSearch(`${dept.code} - ${dept.name}`);
    setShowDepartmentSuggestions(false);
  };

  const handleDepartmentInputChange = (e) => {
    setDepartmentSearch(e.target.value);
    setSelectedDepartment('');
    setSelectedDepartmentCode('');
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setCitySearch(city);
    setShowCitySuggestions(false);
    // Reset address fields when selecting a new city
    setStreetNumber('');
    setStreetName('');
    setPostalCode('');
  };

  const handleCityInputChange = (e) => {
    setCitySearch(e.target.value);
    setSelectedCity('');
  };

  // Prepare dropdown options
  const countryOptions = [
    { value: 'FR', label: 'France' },
    { value: 'DZ', label: 'Algérie' }
  ];

  return (
    <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: 'var(--color-grey-light)', borderRadius: '8px' }}>
      <h3 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>Recherche d'adresse alternative</h3>
      
      {/* Country Selector */}
      <div className="formField" style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: '500' }}>
          Pays *
        </label>
        <CustomDropdown
          options={countryOptions}
          value={selectedCountry}
          onChange={handleCountrySelect}
          placeholder="Sélectionnez un pays"
        />
      </div>

      {/* Department/Wilaya Selector with Search */}
      {selectedCountry && Object.keys(departments).length > 0 && (
        <div className="formField" style={{ position: 'relative', marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: '500' }}>
            {selectedCountry === 'Algérie' ? 'Wilaya *' : 'Département *'}
          </label>
          <input
            type="text"
            value={departmentSearch}
            onChange={handleDepartmentInputChange}
            onFocus={() => setShowDepartmentSuggestions(true)}
            onBlur={() => setTimeout(() => setShowDepartmentSuggestions(false), 200)}
            placeholder={selectedCountry === 'Algérie' ? "Commencez à taper le code ou nom de la wilaya..." : "Commencez à taper le code ou nom du département..."}
            autoComplete="off"
            style={{
              
              padding: '0.75rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
          
          {/* Department/Wilaya Suggestions Dropdown */}
          {showDepartmentSuggestions && filteredDepartments.length > 0 && (
            <ul
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '250px',
                overflowY: 'auto',
                listStyle: 'none',
                padding: 0,
                margin: '4px 0 0 0',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              {filteredDepartments.map((dept) => (
                <li
                  key={dept.code}
                  onClick={() => handleDepartmentSelect(dept)}
                  style={{
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {dept.code} - {dept.name}
                </li>
              ))}
            </ul>
          )}
          
          {showDepartmentSuggestions && departmentSearch && !selectedDepartment && filteredDepartments.length === 0 && (
            <p style={{ color: '#999', marginTop: '0.5rem', fontSize: '0.9rem' }}>
              {selectedCountry === 'Algérie' ? 'Aucune wilaya trouvée' : 'Aucun département trouvé'}
            </p>
          )}
        </div>
      )}

      {/* City Search */}
      {selectedDepartmentCode && cities.length > 0 && (
        <div className="formField" style={{ position: 'relative', marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: '500' }}>
            Ville *
          </label>
          <input
            type="text"
            value={citySearch}
            onChange={handleCityInputChange}
            onFocus={() => setShowCitySuggestions(true)}
            onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
            placeholder="Commencez à taper le nom de la ville..."
            autoComplete="off"
            style={{
              
              padding: '0.75rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '1rem',
            }}
          />
          
          {/* City Suggestions Dropdown */}
          {showCitySuggestions && filteredCities.length > 0 && (
            <ul
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto',
                listStyle: 'none',
                padding: 0,
                margin: '4px 0 0 0',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              {filteredCities.map((city, index) => (
                <li
                  key={index}
                  onClick={() => handleCitySelect(city)}
                  style={{
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    borderBottom: index < filteredCities.length - 1 ? '1px solid #f0f0f0' : 'none',
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {city}
                </li>
              ))}
            </ul>
          )}
          
          {citySearch && filteredCities.length === 0 && (
            <p style={{ color: '#999', marginTop: '0.5rem', fontSize: '0.9rem' }}>
              Aucune ville trouvée
            </p>
          )}
        </div>
      )}

      {/* Selected Address Display */}
      {selectedCity && (
        <div style={{ 
          marginTop: '1.5rem', 
          padding: '1rem', 
          backgroundColor: 'white', 
          borderRadius: '4px', 
          border: '1px solid #ddd' 
        }}>
          <strong style={{ fontSize: '0.95rem' }}>Ville sélectionnée:</strong>
          <p style={{ margin: '0.5rem 0 1rem 0', color: '#333' }}>
            {selectedCity}, {departments[selectedDepartmentCode]}, {selectedCountry}
          </p>

          {/* Street Number */}
          <div className="formField" style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: '500' }}>
              Numéro de rue{selectedCountry === 'France' ? ' *' : ' (optionnel)'}
            </label>
            <input
              type="text"
              value={streetNumber}
              onChange={(e) => setStreetNumber(e.target.value)}
              placeholder="Ex: 10"
              style={{
                
                padding: '0.75rem 1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            />
          </div>

          {/* Street Name */}
          <div className="formField" style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: '500' }}>
              Nom de rue{selectedCountry === 'France' ? ' *' : ' (optionnel)'}
            </label>
            <input
              type="text"
              value={streetName}
              onChange={(e) => setStreetName(e.target.value)}
              placeholder="Ex: Rue de la République"
              style={{
                
                padding: '0.75rem 1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            />
          </div>

          {/* Postal Code */}
          <div className="formField" style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: '500' }}>
              Code postal{selectedCountry === 'France' ? ' *' : ' (optionnel)'}
            </label>
            <input
              type="text"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              placeholder="Ex: 75001"
              style={{
                padding: '0.75rem 1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
              }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: 'none',
                border: '1px solid var(--color-grey)',
                borderRadius: '1rem',
                color: 'var(--color-grey-dark)',
                cursor: 'pointer',
                fontSize: '1rem',
                padding: '0.8rem 1.5rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={async () => {
                setIsAdding(true);
                await onAdd({
                  streetNb: streetNumber,
                  streetName,
                  postalCode,
                  city: selectedCity,
                  adminLevel2: departments[selectedDepartmentCode],
                  adminLevel1: departments[selectedDepartmentCode],
                  country: selectedCountry,
                  countryCode: selectedCountry === 'France' ? 'FR' : 'DZ'
                });
                setIsAdding(false);
              }}
              disabled={isAdding || !selectedCity || (selectedCountry === 'France' && (!streetNumber || !streetName || !postalCode))}
              style={{
                backgroundColor: (isAdding || !selectedCity || (selectedCountry === 'France' && (!streetNumber || !streetName || !postalCode))) ? '#ccc' : 'var(--color-secondary)',
                border: 'none',
                borderRadius: '1rem',
                color: 'white',
                cursor: (isAdding || !selectedCity || (selectedCountry === 'France' && (!streetNumber || !streetName || !postalCode))) ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                padding: '0.8rem 1.5rem',
                transition: 'all 0.3s ease-in',
                opacity: (isAdding || !selectedCity || (selectedCountry === 'France' && (!streetNumber || !streetName || !postalCode))) ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!((isAdding || !selectedCity || (selectedCountry === 'France' && (!streetNumber || !streetName || !postalCode))))) {
                  e.target.style.backgroundColor = 'var(--color-primary-orange)';
                }
              }}
              onMouseLeave={(e) => {
                if (!((isAdding || !selectedCity || (selectedCountry === 'France' && (!streetNumber || !streetName || !postalCode))))) {
                  e.target.style.backgroundColor = 'var(--color-secondary)';
                }
              }}
            >
              {isAdding ? 'Ajout en cours...' : "Ajouter l'adresse"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const EditAccountPage = () => {
  const navigate = useNavigate();
  const addressesRef = useRef(null);
  const [cardColor, setCardColor] = useState(true);
  const [cardInfo2, setCardInfo2] = useState(false);

  const [updateOwner] = useMutation(UPDATE_OWNER);
  const [deleteOwner] = useMutation(DELETE_OWNER);
  const [getImgURL] = useMutation(GET_IMG_URL);

  /**
   * Geocode address using Nominatim API
   * Returns { lat, lon } or null if not found/error
   */
  const geocodeAddress = async (address) => {
    try {
      // Build the query string from address parts
      const parts = [
        address.streetNb,
        address.streetName,
        address.postalCode,
        address.city,
        address.country
      ].filter(Boolean); // Remove empty parts

      const queryString = parts.join(',');
      const encodedQuery = encodeURIComponent(queryString);
      const url = `https://nominatim.openstreetmap.org/search?q=${encodedQuery}&format=json&limit=1`;

      const response = await fetch(url, {
        headers: {
          'User-Agent': 'CarteVerte/1.0' // Nominatim requires a User-Agent
        }
      });

      if (!response.ok) {
        console.error('Nominatim API error:', response.status);
        return null;
      }

      const data = await response.json();

      if (data && data.length > 0 && data[0].lat && data[0].lon) {
        return {
          lat: data[0].lat,
          lon: data[0].lon
        };
      }

      console.warn('No geocoding results found for:', queryString);
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // get owner profile with "query" "meOwner"
  const { loading, error, data } = useQuery(ME_OWNER);
  let oldName = "",
    oldDisplayName = "",
    oldEmail = "",
    oldLogo = "",
    oldColor = "",
    oldTextColor = "",
    oldPhone = "",
    // oldSiret = "",
    // oldStreetNb = "",      // Removed - using new address structure
    // oldStreetName = "",    // Removed - using new address structure
    // oldPostalCode = "",    // Removed - using new address structure
    // oldCity = "",          // Removed - using new address structure
    oldAddresses = "",
    oldCommerceType = "",
    oldOffer = "",
    oldOfferMsgs = "",
    oldIsActive = "";

  // refresh state data
  const [name, setName] = useState(undefined);
  const [displayName, setDisplayName] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [logo, setLogo] = useState(undefined);
  const [logoMethod, setLogoMethod] = useState("upload");
  const [color, setColor] = useState("");
  const [textColor, setTextColor] = useState("");
  const [phone, setPhone] = useState(undefined);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [commerceType, setCommerceType] = useState("0");
  const [offer, setOffer] = useState("0");
  const [offerMsgs, setOfferMsgs] = useState(undefined);
  const [offerMsgsCheckboxChecked, setOfferMsgsCheckboxChecked] =
    useState(true);
  const [defaultCheckedValue, setDefaultCheckedValue] = useState(true);

  const [isActive, setIsActive] = useState();

  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // SearchableAddressSelector visibility
  const [showAddressSelector, setShowAddressSelector] = useState(false);

  // Toggle password visibility functions
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  // Helper function to parse pipe-separated address format
  // Format: streetNb|streetName|postalCode|city|adminLevel2|adminLevel1|countryCode
  const convertOldAddressFormat = (addresses) => {
    // If it's already an array, return it
    if (Array.isArray(addresses)) {
      return addresses;
    }

    // If it's a string, parse the pipe-separated format
    if (typeof addresses === 'string' && addresses.length > 0) {
      return addresses.split(';!#').map((addr, index) => {
        // Check if address contains lat/lon (double pipe separator)
        let parts = addr.split('|');
        let lat = '';
        let lon = '';
        
        // Check for double pipe pattern: ...countryCode||lat|lon
        const doublePipeIndex = addr.indexOf('##');
        if (doublePipeIndex !== -1) {
          // Split at double pipe
          const beforeCoords = addr.substring(0, doublePipeIndex);
          const afterCoords = addr.substring(doublePipeIndex + 2);
          parts = beforeCoords.split('|');
          const coordParts = afterCoords.split('|');
          lat = coordParts[0] || '';
          lon = coordParts[1] || '';
        }

        // Handle both old simple format (just city name) and new pipe format
        if (parts.length === 1) {
          // Old legacy format - just city name
          return {
            id: `addr_${index}`,
            city: parts[0],
            country: '',
            adminLevel1: '',
            adminLevel2: '',
            streetNb: '',
            streetName: '',
            postalCode: '',
            countryCode: '',
            lat,
            lon
          };
        }

        // New pipe format: streetNb|streetName|postalCode|city|adminLevel2|adminLevel1|countryCode
        return {
          id: `addr_${index}`,
          streetNb: parts[0] || '',
          streetName: parts[1] || '',
          postalCode: parts[2] || '',
          city: parts[3] || '',
          adminLevel2: parts[4] || '',
          adminLevel1: parts[5] || '',
          countryCode: parts[6] || '',
          country: parts[6] || '', // countryCode used as country for display
          lat,
          lon
        };
      });
    }

    // Empty case
    return [];
  };

  // State to track current addresses
  const [currentAddresses, setCurrentAddresses] = useState([]);
  const [deletePopinAddress, setDeletePopinAddress] = useState(null);

  // Hex input display state: keep raw hex digits and whether user included a leading '#'
  const [hexInput, setHexInput] = useState("");
  const [hexHasHash, setHexHasHash] = useState(true);

  // Initialize addresses when data loads
  useEffect(() => {
    if (data?.meOwner?.addresses) {
      setCurrentAddresses(convertOldAddressFormat(data.meOwner.addresses));
    }
  }, [data]);

  // Keep hex input synced with color/textColor state so the input shows current value
  useEffect(() => {
    const current = cardColor ? color || oldColor || "#0caa41" : textColor || oldTextColor || "#000000";
    if (current) {
      if (current.startsWith("#")) {
        setHexHasHash(true);
        setHexInput(current.slice(1));
      } else {
        setHexHasHash(false);
        setHexInput(current);
      }
    }
  }, [color, textColor, cardColor, oldColor, oldTextColor]);

  // Handle address updates from AddressManager
  const handleAddressesUpdate = (updatedAddresses) => {
    setCurrentAddresses(updatedAddresses);
  };

  if (loading)
    return (
      <div className="loaderParent">
        <div className="loader"></div>
      </div>
    );
  else if (error || !data.meOwner.email) {
    sessionStorage.removeItem("loggedIn");
    navigate("/login");
  } else {
    oldName = data.meOwner.name;
    oldDisplayName = data.meOwner.displayName;
    oldEmail = data.meOwner.email;
    oldLogo = data.meOwner.logo;
    oldColor = data.meOwner.color;
    oldTextColor = data.meOwner.textColor;
    oldPhone = data.meOwner.phone;
    oldAddresses = data.meOwner.addresses;
    oldCommerceType = data.meOwner.commerceType;
    oldOffer = data.meOwner.offer;
    oldOfferMsgs = data.meOwner.offerMsgs;
    oldIsActive = data.meOwner.isActive;
  }

  const handleColorPicked = (color) => {
    cardColor ? setColor(color) : setTextColor(color);
  };
  const squaresRow1 = [];
  const squaresRow2 = [];
  const squaresRow3 = [];
  const offer10squares1 = offer == 1 || (offer == "0" && oldOffer == 1);
  const offer10squares2 = offer == 2 || (offer == "0" && oldOffer == 2);
  const offer10squares3 = offer == 3 || (offer == "0" && oldOffer == 3);
  const offer15squares1 = offer == 5 || (offer == "0" && oldOffer == 5);
  const offer15squares2 = offer == 6 || (offer == "0" && oldOffer == 6);
  let size = offer15squares1 || offer15squares2 ? "3.3rem" : "4rem";

  for (let i = 0; i < 5; i++) {
    if ((logo || oldLogo) && (i == 0 || i == 1 || i == 2))
      squaresRow1.push(
        <img
          key={i}
          className="cardSquare cardSq"
          style={{ height: size, width: size }}
          src={logo || oldLogo}
          alt=""
        ></img>
      );
    else
      squaresRow1.push(
        <div
          key={i}
          className="cardSquare"
          style={{
            border: `solid ${textColor || oldTextColor || "#000000"}`,

            height: size,
            width: size,
          }}
        >
          {i == 4 && (offer10squares1 || offer15squares1 || offer15squares2)
            ? "%"
            : ""}
        </div>
      );
  }
  for (let i = 0; i < 5; i++) {
    squaresRow2.push(
      <div
        key={i * 2}
        className="cardSquare"
        style={{
          border: `solid ${textColor || oldTextColor || "#000000"}`,

          height: size,
          width: size,
        }}
      >
        {i == 4 && (offer10squares3 || offer15squares1 || offer15squares2)
          ? "%"
          : ""}
        <span
          key={i * 2}
          style={{
            color: textColor || oldTextColor || "#000000",
            fontWeight: "bold",
          }}
        >
          {(offer10squares1 || offer10squares2) && i == 4 ? "FREE" : ""}
        </span>
      </div>
    );
  }
  if (offer15squares1 || offer15squares2)
    for (let i = 0; i < 5; i++) {
      squaresRow3.push(
        <div
          key={i * 3}
          className="cardSquare"
          style={{
            border: `solid ${textColor || oldTextColor || "#000000"}`,

            height: size,
            width: size,
          }}
        >
          {i == 4 && offer15squares2 ? "%" : ""}
          <span
            key={i * 3}
            style={{
              color: textColor || oldTextColor || "#000000",
              fontWeight: "bold",
            }}
          >
            {i == 4 && offer15squares1 ? "FREE" : ""}
          </span>
        </div>
      );
    }

  const defaultOfferMsgs = [
    "Offre",
    `10ème conso gratuite et réduction sur la 5ème`,
    "10ème conso gratuite",
    "Réduction sur la 10ème conso ",
    "11ème conso gratuite",
    "15ème conso gratuite et réduction sur la 5ème & la 10ème",
    "Réduction sur la 5ème, 10ème & 15ème conso",
  ];

  const offerMsgsPlaceholders = [
    [
      //type de commerce : restauration
      [
        //offre1
        "Exemple : 5ème achat : boisson offerte", //5ème case
        "Exemple : 10ème achat : menu offert", //10 case
        "", //15ème case
      ],
      ["", "Exemple : 10ème achat : menu offert", ""], //offre2
      ["", "Exemple : 10ème achat : -50% sur le menu", ""], //offre3
      ["", "Exemple : 11ème achat : menu offert", ""], //offre4
      [
        //offre5
        "Exemple : 5ème achat : boisson offerte",
        "Exemple : 10ème achat : déssert offert",
        "Exemple : 15ème achat : menu offert",
      ],
      [
        //offre6
        "Exemple : 5ème achat : boisson offerte",
        "Exemple : 10ème achat : déssert offert",
        "Exemple : 15ème achat : boisson et déssert offerts",
      ],
    ],
    [
      //type de commerce : coiffure
      [
        "Exemple : 5ème coiffure : -25%",
        "Exemple : 10ème coiffure offerte",
        "",
      ],
      ["", "Exemple : 10ème coiffure offerte", ""],
      ["", "Exemple : 10ème coiffure : -50%", ""],
      ["", "Exemple : 11ème coiffure offerte", ""],
      [
        "Exemple : 5ème coiffure : -25%",
        "Exemple : 10ème coiffure : -25%",
        "Exemple : 15ème coiffure offerte",
      ],
      [
        "Exemple : 5ème coiffure : -25%",
        "Exemple : 10ème coiffure : -25%",
        "Exemple : 15ème coiffure : -50%",
      ],
    ],
    [
      //type de commerce : sport
      ["Exemple : 5ème séance : -25%", "Exemple : 10ème séance offerte", ""],
      ["", "Exemple : 10ème séance offerte", ""],
      ["", "Exemple : 10ème séance : -50%", ""],
      ["", "Exemple : 11ème séance offerte", ""],
      [
        "Exemple : 5ème séance : -25%",
        "Exemple : 10ème séance : -25%",
        "Exemple : 15ème séance offerte",
      ],
      [
        "Exemple : 5ème séance : -25%",
        "Exemple : 10ème séance : -25%",
        "Exemple : 15ème séance : -50%",
      ],
    ],
    [
      //type de commerce : services
      [
        "Exemple : 5ème entretien : -25%",
        "Exemple : 10ème entretien offert",
        "",
      ],
      ["", "Exemple : 10ème entretien offert", ""],
      ["", "Exemple : 10ème entretien : -50%", ""],
      ["", "Exemple : 11ème entretien offert", ""],
      [
        "Exemple : 5ème entretien : -25%",
        "Exemple : 10ème entretien : -25%",
        "Exemple : 15ème entretien offert",
      ],
      [
        "Exemple : 5ème entretien : -25%",
        "Exemple : 10ème entretien : -25%",
        "Exemple : 15ème entretien : -50%",
      ],
    ],
    [
      //type de commerce : autre
      ["", "", ""],
      ["", "", ""],
      ["", "", ""],
      ["", "", ""],
      ["", "", ""],
      ["", "", ""],
    ],
  ];

  const handleofferMsgsPlaceholders = (idx) => {
    return offerMsgsPlaceholders[
      parseInt(commerceType !== "0" ? commerceType : oldCommerceType || 1) - 1
    ][parseInt(offer !== "0" ? offer : oldOffer || 1) - 1][idx];
  };

  const handleMutationStatus = (status, error = undefined) => {
    document.querySelector("#mutationStatus p").innerText =
      status == "error" ? error : `Votre compte a été mis à jour avec succès`;

    document.querySelector("#mutationStatus").style.top = 0;
    document.querySelector("#mutationStatus").style.backgroundColor =
      status == "error" ? "var(--color-inactive)" : "var(--color-active)";

    setTimeout(() => {
      editElem(".saveAccountBtn", "disabled", false);
      editElem(".smallLoader2", "display", "none");
      if (status !== "error") window.location.href = "/mon-compte";
      if (status == "error" && !!document.querySelector("#mutationStatus"))
        document.querySelector("#mutationStatus").style.top = "-6.2rem";
    }, 3000);
  };

  const handleOfferMsgs = (checked, offer, timeout) => {
    document.querySelectorAll(".squareMsg").forEach((elem) => {
      elem.style.opacity = 0;
    });

    setTimeout(() => {
      document.querySelectorAll(".squareMsg").forEach((elem) => {
        elem.style.display = "none";
      });
      if (checked) {
        if (offer == 1 || (offer == "0" && oldOffer == 1)) {
          editElem("#square5Msg", "display", "block");
          editElem("#square10Msg", "display", "block");
          setTimeout(() => {
            editElem("#square5Msg", "opacity", 1);
            editElem("#square10Msg", "opacity", 1);
          }, 50);
        }
        if (
          offer == 2 ||
          (offer == "0" && oldOffer == 2) ||
          offer == 3 ||
          (offer == "0" && oldOffer == 3)
        ) {
          setTimeout(() => {
            editElem("#square10Msg", "opacity", 1);
          }, 50);
          editElem("#square10Msg", "display", "block");
        }
        if (offer == 4 || (offer == "0" && oldOffer == 4)) {
          setTimeout(() => {
            editElem("#square11Msg", "opacity", 1);
          }, 50);
          editElem("#square11Msg", "display", "block");
        }
        if (
          offer == 5 ||
          (offer == "0" && oldOffer == 5) ||
          offer == 6 ||
          (offer == "0" && oldOffer == 6)
        ) {
          editElem("#square5Msg", "display", "block");
          editElem("#square10Msg", "display", "block");
          editElem("#square15Msg", "display", "block");
          setTimeout(() => {
            editElem("#square5Msg", "opacity", 1);
            editElem("#square10Msg", "opacity", 1);
            editElem("#square15Msg", "opacity", 1);
          }, 50);
        }
      }
    }, timeout);
  };

  const buildOfferMsgs = (idx, value) => {
    let arr;
    if (offerMsgs) arr = offerMsgs.split(";!#");
    else {
      setOfferMsgs(oldOfferMsgs);
      arr = oldOfferMsgs.split(";!#");
    }
    arr[idx] = value;
    arr = arr.join().replaceAll(",", ";!#");
    setOfferMsgs(arr);
  };

  /* OLD ADDRESS MODAL FUNCTIONS - Replaced by AddressManager component
  const closeModal = () => {
    editElem(".overlay", "opacity", "0");
    setTimeout(() => {
      editElem(".overlay", "visibility", "hidden");
    }, 500);
    editElem(".addressModal", "width", "0");
    editElem("#modalContent", "opacity", "0");
  };

  const showModal = () => {
    editElem(".overlay", "opacity", ".6");
    editElem(".overlay", "visibility", "visible");
    let width = "45%";
    if (window.screen.width < 901) width = "100%";
    editElem(".addressModal", "width", width);
    setTimeout(() => {
      editElem("#modalContent", "opacity", "1");
    }, 300);
  };

  const addAddress = () => {
    const streetNb = document.querySelector("#streetNb").value;
    const streetName = document.querySelector("#streetName").value;
    const postalCode = document.querySelector("#postalCode").value;
    const city = document.querySelector("#city").value;
    if (!streetNb || !streetName || !postalCode || !city) {
      editElem(".emptyField", "display", "block");
    } else if (postalCode.toString().length < 5) {
      editElem(".postalCodeError", "display", "block");
    } else {
      document.querySelector("#addressesList").insertAdjacentHTML(
        "beforeend",
        `<li class="addressFieldParent"><input class="addressFieldInput" value="${streetNb} ${streetName}, ${postalCode} ${city}" />
      <svg onClick="this.parentElement.remove()" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="trash" class="svg-inline--fa fa-trash fa-w-14 deleteAddress" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"></path></svg>
      </li>`
      );

      closeModal();
      document.querySelector("#streetNb").value = "";
      document.querySelector("#streetName").value = "";
      document.querySelector("#postalCode").value = "";
      document.querySelector("#city").value = "";
    }
  };

  const buildAddress = () => {
    let html = [];
    if (oldAddresses) {
      oldAddresses.split(";!#").forEach((oldAddress, idx) => {
        let li = (
          <li className="addressFieldParent" key={idx}>
            <input className="addressFieldInput" defaultValue={oldAddress} />
            <FontAwesomeIcon
              icon={faTrash}
              className="deleteAddress"
              onClick={(e) => e.target.closest(".addressFieldParent").remove()}
            />
          </li>
        );
        html.push(li);
      });
    }

    return html;
  };
  END OLD FUNCTIONS */

  document.title = "Carte Verte : Modifier mon compte";
  return (
    <div id="editAccountPage">
      <div id="mutationStatus">
        <p>Votre compte est mis à jour avec succès</p>
      </div>
      {/* OLD ADDRESS MODAL - Replaced by AddressManager component
      <div className="overlay" onClick={closeModal}></div>
      <div className="addressModal">
        <div id="modalContent">
          <FontAwesomeIcon
            id="modalClose"
            icon={faArrowLeft}
            onClick={closeModal}
            style={{
              fontSize: "2rem",
              margin: "1.5rem 0 0 3rem",
              cursor: "pointer",
            }}
          />
          <p id="modalAction" style={{ textAlign: "center" }}>
            Ajouter une adresse :{" "}
          </p>
          <div id="modalAddresses">
            <div id="street">
              <p className="formField">
                N° de rue
                <input
                  type="text"
                  // pattern="[a-z][0-9]*"
                  // onKeyPress={(e) => {
                  //   if (isNaN(parseInt(e.key))) e.preventDefault();
                  // }}
                  id="streetNb"
                  onChange={(event) => {
                    setStreetNb(event.target.value);
                    editElem(".emptyField", "display", "none");
                  }}
                />
              </p>
              <p className="formField">
                Nom de rue
                <input
                  type="text"
                  id="streetName"
                  onChange={(event) => {
                    setStreetName(event.target.value);
                    editElem(".emptyField", "display", "none");
                  }}
                />
              </p>
            </div>
            <div>
              <p className="formField">
                Code postal
                <input
                  type="text"
                  id="postalCode"
                  onKeyPress={(e) => {
                    if (isNaN(parseInt(e.key))) e.preventDefault();
                  }}
                  maxLength="5"
                  onChange={(event) => {
                    setPostalCode(event.target.value);
                    editElem(".postalCodeError", "display", "none");
                    editElem(".emptyField", "display", "none");
                  }}
                />
              </p>
              <p className="error postalCodeError">
                Le code postal doit comporter 5 chiffres
              </p>
              <p className="formField">
                Ville
                <input
                  type="text"
                  id="city"
                  onChange={(event) => {
                    setCity(event.target.value);
                    editElem(".emptyField", "display", "none");
                  }}
                />
              </p>
            </div>
            <button id="addAddressModal" onClick={addAddress}>
              Ajouter une adresse
            </button>
            <p className="error emptyField" style={{ textAlign: "center" }}>
              Veillez remplir tous les champs !
            </p>
          </div>
        </div>
      </div>
      END OLD MODAL */}
      <NavbarAccount title={"MON COMPTE"} isActive={oldIsActive} />
      <div id="editAccountContainer">
        <Link className="returnLink" to="/mon-compte">
          <FontAwesomeIcon
            icon={faArrowLeft}
            style={{ fontSize: "1.4rem", marginRight: "1rem" }}
          />
          Mon compte
        </Link>
        <div id="contact">
          <div id="credentials">
            <h1>Identifiants</h1>
            <span>Tous les champs sont optionnels pour la modification.</span>
            <p className="formField">
              Nom du commerce*
              <input
                type="text"
                id="name"
                value={name !== undefined ? name : oldName}
                onChange={(event) => setName(event.target.value)}
              />
            </p>
            <p className="formField">
              E-mail*
              <input
                type="text"
                id="email"
                placeholder={oldEmail}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled
              />
            </p>
            <div className="formField">
              <label>Nouveau mot de passe</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="newPassword"
                  placeholder="********"
                  value={password}
                  onChange={(event) => {
                    editElem(".newPasswordLengthError", "display", "none");
                    editElem(".newPasswordMatchError", "display", "none");
                    setPassword(event.target.value);
                  }}
                />
                <span
                  onClick={togglePasswordVisibility}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#666',
                    fontSize: '1.2rem',
                    userSelect: 'none'
                  }}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </span>
              </div>
            </div>
            <p className="error newPasswordLengthError">
              Le mot de passe doit contenir au moins 8 caractères
            </p>
            <p className="error newPasswordMatchError">
              Les deux mots de passe ne correspndent pas
            </p>
            <div className="formField">
              <label>Confirmation de mot de passe</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="confirmPassword"
                  placeholder="********"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                />
                <span
                  onClick={toggleNewPasswordVisibility}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#666',
                    fontSize: '1.2rem',
                    userSelect: 'none'
                  }}
                >
                  <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                </span>
              </div>
            </div>
          </div>
          <div id="contactInfo">
            <h1>Coordonées du magasin</h1>
            <span>Tous les champs sont optionnels pour la modification.</span>
            <div className="formField">
              <label>Téléphone*</label>
              <input
                type="tel"
                id="phone"
                placeholder={oldPhone || "Votre numéro de téléphone"}
                value={phone !== undefined ? phone : oldPhone}
                onChange={(e) => setPhone(e.target.value)}
                //onChange={(formattedPhone) => setPhone(formattedPhone)}
                //onValidationChange={(isValid) => setIsPhoneValid(isValid)}
              />
            </div>
            <p className="error phoneNumberError" style={{ display: !isPhoneValid ? 'block' : 'none' }}>
              Le numéro de téléphone n'est pas valide pour ce pays !
            </p>
            <p className="formField">
              <label
                htmlFor="shopTypes"
                style={{
                  display: "block",
                  marginBottom: ".5rem",
                  marginTop: "3rem",
                }}
              >
                Type du commerce*
              </label>
              <select
                id="shopTypes"
                onChange={(e) => setCommerceType(e.target.value)}
                defaultValue={oldCommerceType}
              >
                <option value="0">Type</option>
                <option value="1">Restauration</option>
                <option value="2">Salon de coiffure</option>
                <option value="3">Salle de sport</option>
                <option value="4">Services</option>
                <option value="5">Autre</option>
              </select>
            </p>
            <div id="addressesDiv">
              <p className="formField" id="myAddresses">
                Adresse(s) du magasin*
              </p>
              {/* List of Addresses */}
              <div className="addressManager" ref={addressesRef}>
                <ul className="addressManager__list" style={{ listStyle: 'none', padding: 0 }}>
                  {currentAddresses.map((address, index) => (
                    <li key={address.id || index} className="addressManager__item">
                      <div className="addressManager__icon-container">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="addressManager__icon" />
                      </div>
                      <div className="addressManager__content">
                        <div className="addressManager__details">
                          {address.streetNb && address.streetName && (
                            <p className="street-info">{address.streetNb} {address.streetName}</p>
                          )}
                          <p>
                            {address.city}
                            {address.postalCode && `, ${address.postalCode}`}
                          </p>
                          {address.adminLevel2 && <p>{address.adminLevel2}</p>}
                          <p><strong>{address.country || 'France'}</strong></p>
                        </div>
                      </div>
                      <div className="addressManager__actions">
                        <button
                          type="button"
                          onClick={() => setDeletePopinAddress(address)}
                          className="addressManager__removeBtn"
                          title="Supprimer"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Button to show Searchable Address Selector */}
              {!showAddressSelector && (
                <div className="searchable-address-btn-container">
                  <button
                    type="button"
                    onClick={() => setShowAddressSelector(true)}
                    className="searchable-address-btn"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Ajouter une adresse
                  </button>
                </div>
              )}
              
              {/* Searchable Address Selector for France */}
              {showAddressSelector && (
                <SearchableAddressSelector 
                  onAdd={async (addressData) => {
                    // Geocode the address
                    const coords = await geocodeAddress(addressData);
                    
                    const newAddress = {
                      id: `new_${Date.now()}`,
                      ...addressData,
                      lat: coords ? coords.lat : null,
                      lon: coords ? coords.lon : null
                    };
                    setCurrentAddresses([...currentAddresses, newAddress]);
                    setShowAddressSelector(false);
                    // Scroll to top of addresses
                    setTimeout(() => {
                      if (addressesRef.current) {
                        addressesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }}
                  onCancel={() => {
                    setShowAddressSelector(false);
                    // Scroll to top of addresses
                    setTimeout(() => {
                      if (addressesRef.current) {
                        addressesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }}
                />
              )}

              {/* Delete Confirmation Popin */}
              {deletePopinAddress && (
                <>
                  <div className="addressManager__overlay" onClick={() => setDeletePopinAddress(null)}></div>
                  <div className="addressManager__popin">
                    <h3>Supprimer cette adresse ?</h3>
                    <p>Cette adresse ne sera plus affichée aux utilisateurs après avoir cliqué sur "Sauvegarder".</p>
                    <div className="addressManager__popinAddress">
                      {deletePopinAddress.streetNb && deletePopinAddress.streetName && (
                        <p>{deletePopinAddress.streetNb} {deletePopinAddress.streetName}</p>
                      )}
                      <p>
                        {deletePopinAddress.city}
                        {deletePopinAddress.postalCode && `, ${deletePopinAddress.postalCode}`}
                      </p>
                      {deletePopinAddress.adminLevel2 && <p>{deletePopinAddress.adminLevel2}</p>}
                      {deletePopinAddress.adminLevel1 && <p>{deletePopinAddress.adminLevel1}</p>}
                      <p><strong>{deletePopinAddress.country || 'France'}</strong></p>
                    </div>
                    <div className="addressManager__popinActions">
                      <button
                        type="button"
                        onClick={() => setDeletePopinAddress(null)}
                        className="addressManager__popinCancel"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const updatedAddresses = currentAddresses.filter(addr => addr.id !== deletePopinAddress.id);
                          setCurrentAddresses(updatedAddresses);
                          setDeletePopinAddress(null);
                        }}
                        className="addressManager__popinConfirm"
                      >
                        Confirmer la suppression
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div id="cardPerso">
          <div id="cardPersoContent">
            <h1 id="cardPersoTitle">Personnalisation de ma carte</h1>
            <span>Tous les champs sont optionnels pour la modification.</span>
            <div id="cardInfo">
              <div id="cardSpecs">
                <div className="card-section card-section--offers">
                  <p className="formField">
                    <label
                      htmlFor="shopOffer"
                      style={{
                        display: "block",
                        marginBottom: ".5rem",
                        marginTop: "3rem",
                      }}
                    >
                      Choix de l'offre pour vos clients*
                    </label>
                    <select
                      id="shopOffer"
                      onChange={(e) => {
                        setOffer(e.target.value);
                        setOfferMsgs(";!#;!#");
                        handleOfferMsgs(
                          document.querySelector("#offerMsgs").checked,
                          e.target.value,
                          500
                        );
                      }}
                      defaultValue={oldOffer}
                    >
                      <option value="0">{defaultOfferMsgs[0]}</option>
                      <option value="1">{defaultOfferMsgs[1]}</option>
                      <option value="2">{defaultOfferMsgs[2]}</option>
                      <option value="3">{defaultOfferMsgs[3]}</option>
                      <option value="4">{defaultOfferMsgs[4]}</option>
                      <option value="5">{defaultOfferMsgs[5]}</option>
                      <option value="6">{defaultOfferMsgs[6]}</option>
                    </select>
                  </p>
                  <p id="offerMsgsCheckboxLabel">
                    <input
                      type="checkbox"
                      id="offerMsgs"
                      name="gender"
                      checked={
                        defaultCheckedValue
                          ? oldOfferMsgs.length > 6
                          : offerMsgsCheckboxChecked
                      }
                      onChange={(event) => {
                        setDefaultCheckedValue(false);
                        setOfferMsgsCheckboxChecked(event.target.checked);
                        const timeout = event.target.checked ? 0 : 500;
                        handleOfferMsgs(event.target.checked, offer, timeout);
                      }}
                    />
                    <label htmlFor="offerMsgs" id="offerMsgsLabel">
                      Je personnalise les messages de l'offre
                      <br />
                      <span>
                        cliquez sur le{" "}
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          style={{ fontSize: "1.2rem", color: "" }}
                        />{" "}
                        qui se trouve sur le verso de la carte pour visualiser les
                        messages
                      </span>
                    </label>
                  </p>
                  <p
                    id="square5Msg"
                    className="formField squareMsg hidden"
                    style={{
                      display:
                        oldOfferMsgs.length > 6 && /1|5|6/.test(oldOffer)
                          ? "block"
                          : "none",
                      opacity:
                        oldOfferMsgs.length > 6 && /1|5|6/.test(oldOffer)
                          ? "1"
                          : "0",
                      marginTop: "2rem",
                    }}
                  >
                    <span>Case 5 :</span>
                    <input
                      type="text"
                      style={{}}
                      placeholder={handleofferMsgsPlaceholders(0)}
                      value={
                        offerMsgs !== undefined
                          ? offerMsgs.split(";!#")[0]
                          : oldOfferMsgs.split(";!#")[0]
                      }
                      onChange={(event) => buildOfferMsgs(0, event.target.value)}
                    />
                  </p>
                  <p
                    id="square10Msg"
                    className="formField squareMsg hidden"
                    style={{
                      display:
                        oldOfferMsgs.length > 6 && /1|2|3|5|6/.test(oldOffer)
                          ? "block"
                          : "none",
                      opacity:
                        oldOfferMsgs.length > 6 && /1|2|3|5|6/.test(oldOffer)
                          ? "1"
                          : "0",
                      marginTop: "2rem",
                    }}
                  >
                    <span>Case 10 :</span>
                    <input
                      type="text"
                      style={{}}
                      placeholder={handleofferMsgsPlaceholders(1)}
                      value={
                        offerMsgs !== undefined
                          ? offerMsgs.split(";!#")[1]
                          : oldOfferMsgs.split(";!#")[1]
                      }
                      onChange={(event) => buildOfferMsgs(1, event.target.value)}
                    />
                  </p>
                  <p
                    id="square11Msg"
                    className="formField squareMsg hidden"
                    style={{
                      display:
                        oldOfferMsgs.length > 6 && oldOffer == "4"
                          ? "block"
                          : "none",
                      opacity:
                        oldOfferMsgs.length > 6 && oldOffer == "4" ? "1" : "0",
                      marginTop: "2rem",
                    }}
                  >
                    <span>Case 11 :</span>
                    <input
                      type="text"
                      style={{}}
                      placeholder={handleofferMsgsPlaceholders(1)}
                      value={
                        offerMsgs !== undefined
                          ? offerMsgs.split(";!#")[1]
                          : oldOfferMsgs.split(";!#")[1]
                      }
                      onChange={(event) => buildOfferMsgs(1, event.target.value)}
                    />
                  </p>
                  <p
                    id="square15Msg"
                    className="formField squareMsg hidden"
                    style={{
                      display:
                        oldOfferMsgs.length > 6 && /5|6/.test(oldOffer)
                          ? "block"
                          : "none",
                      opacity:
                        oldOfferMsgs.length > 6 && /5|6/.test(oldOffer)
                          ? "1"
                          : "0",
                      marginTop: "2rem",
                    }}
                  >
                    <span>Case 15 :</span>
                    <input
                      type="text"
                      style={{}}
                      placeholder={handleofferMsgsPlaceholders(2)}
                      value={
                        offerMsgs !== undefined
                          ? offerMsgs.split(";!#")[2]
                          : oldOfferMsgs.split(";!#")[2]
                      }
                      onChange={(event) => buildOfferMsgs(2, event.target.value)}
                    />
                  </p>

                </div>{/* end .card-section--offers */}

                <div className="card-section card-section--logo">
                  <div style={{ marginTop: "1rem" }}>
                    <p style={{ marginBottom: ".5rem" }}>Logo*</p>
                    <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                      <label style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                        <input type="radio" name="logoMethod" value="upload" checked={logoMethod === 'upload'} onChange={() => setLogoMethod('upload')} />
                        Télécharger
                      </label>

                      <label style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                        <input type="radio" name="logoMethod" value="url" checked={logoMethod === 'url'} style={{ width: "max-content" }} onChange={() => setLogoMethod('url')} />
                        Utiliser une URL
                      </label>
                    </div>

                    <div style={{ backgroundColor: "var(--color-grey-light)", marginTop: "1rem", padding: "1rem", borderRadius: "8px" }}>
                      {logoMethod === 'upload' ? (
                        <div style={{ position: "relative" }}>
                          {/* Hidden native file input; label (French) triggers it and preserves click/change handlers */}
                          <input
                            type="file"
                            id="filePicker"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              editElem(".smallLoaderParent", "display", "block");
                              const file = e.target.files[0];
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                if (("reader res", reader.result.length > 5000000)) {
                                  editElem(".imgBBSizeError", "display", "block");
                                } else {
                                  getImgURL({
                                    variables: {
                                      data: {
                                        name: name || oldName,
                                        base64string: reader.result.split("base64,")[1],
                                      },
                                    },
                                  })
                                    .then((res) => {
                                      setLogo(res.data.getImgUrl);
                                      editElem(".smallLoaderParent", "display", "none");
                                      editElem(".imgBBError", "display", "none");
                                    })
                                    .catch((error) => {
                                      editElem(".smallLoaderParent", "display", "none");
                                      editElem(".imgBBError", "display", "block");
                                      console.error("error", error);
                                    });
                                }
                              };
                              if (file) reader.readAsDataURL(file);
                            }}
                          />
                          <div className="logo-input">
                            <label
                              htmlFor="filePicker"
                              onClick={(e) => {
                                editElem(".imgBBError", "display", "none");
                                editElem(".imgBBSizeError", "display", "none");
                              }}
                              className="filePickerLabel"
                            >
                              Choisir un fichier
                            </label>

                            <div className="logo-preview">
                              {(logo || oldLogo) ? (
                                <img src={logo || oldLogo} alt="Aperçu du logo" />
                              ) : (
                                <div className="logo-placeholder" style={{
                                  width: '100px',
                                  height: '100px',
                                  border: '2px dashed #ccc',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                  color: '#999',
                                  fontSize: '0.9rem',
                                  textAlign: 'center',
                                  backgroundColor: '#f9f9f9'
                                }}>
                                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>�️</div>
                                  <div>Aperçu du logo</div>
                                </div>
                              )}
                            </div>

                            <div className="smallLoaderParent">
                              <div className="smallLoader"></div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: "1.2rem" }}>
                          Entrez l'URL publique du logo
                          <input
                            type="text"
                            id="logo"
                            style={{ backgroundColor: "var(--color-grey-light)" }}
                            placeholder={oldLogo || "https://exemple.com/images/monLogo.png"}
                            value={logo !== undefined ? logo : oldLogo ? oldLogo : ""}
                            onChange={(event) => setLogo(event.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="error imgBBError">
                    Une erreur inconnue est survenue. Veuillez recommencez !
                  </p>
                  <p className="error imgBBSizeError">
                    La taille du logo ne doit pas dépasser les 10MB !
                  </p>

                  <p className="error logoError">
                    La taille du logo est trop grande
                  </p>
                  <p id="offerMsgsCheckboxLabel">
                    <input
                      type="checkbox"
                      id="displayName"
                      name="displayName"
                      checked={
                        displayName !== undefined ? displayName : oldDisplayName
                      }
                      onChange={(event) => {
                        setDisplayName(event.target.checked);
                      }}
                    />
                    <label htmlFor="displayName" id="displayNameLabel">
                      Afficher le nom du commerce en plus du logo sur ma carte
                    </label>
                  </p>
                </div>{/* end .card-section--logo */}


                <div
                  className="colorDiv"
                  style={{ marginBottom: "1rem", marginTop: "6rem" }}
                >
                  <div className="colorBtns" style={{ marginBottom: ".8rem" }}>
                    <p
                      onClick={() => {
                        document.querySelector(".switch").click();
                      }}
                      style={{
                        color: cardColor
                          ? "var(--color-primary-orange)"
                          : "var(--color-grey)",
                        cursor: "pointer",
                        display: "inline",
                        fontWeight: cardColor ? "bold" : "",
                        opacity: cardColor ? "1" : ".6",
                      }}
                    >
                      Couleur de fond*
                    </p>
                    <label className="switch">
                      <input
                        type="checkbox"
                        onChange={() => setCardColor(!cardColor)}
                      />
                      <span className="slider round"></span>
                    </label>
                    <p
                      onClick={() => {
                        document.querySelector(".switch").click();
                      }}
                      style={{
                        color: !cardColor
                          ? "var(--color-primary-orange)"
                          : "var(--color-grey)",
                        cursor: "pointer",
                        display: "inline",
                        fontWeight: !cardColor ? "bold" : "",
                        opacity: !cardColor ? "1" : ".6",
                      }}
                    >
                      Couleur de texte*
                    </p>
                  </div>
                  <HexColorPicker
                    color={
                      cardColor
                        ? color || oldColor || "#0caa41"
                        : textColor || oldTextColor || "#000000"
                    }
                    onChange={handleColorPicked}
                  />
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginTop: '1rem',
                    justifyContent: 'center'
                  }}>
                    <label style={{ fontSize: '1rem', fontWeight: '500' }}>Hex:</label>
                    <input
                      type="text"
                      value={(hexHasHash ? "#" : "") + (hexInput || "")}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = String(e.clipboardData.getData("text") || "").trim();
                        // Remove whitespace and keep only hex digits and optional '#'
                        let cleaned = pasted.replace(/[^0-9A-Fa-f#]/g, "");
                        const containsHash = cleaned.includes("#");
                        // Remove all hashes then take up to 6 hex digits
                        const hexOnly = cleaned.replace(/#/g, "").slice(0, 6);
                        setHexHasHash(containsHash);
                        setHexInput(hexOnly);
                        if (hexOnly.length > 0) handleColorPicked("#" + hexOnly);
                      }}
                      onChange={(e) => {
                        let value = String(e.target.value || "").trim();
                        // Detect presence of leading '#'
                        const hasHash = value.startsWith("#");
                        // Remove any non-hex characters (and '#')
                        const hex = value.replace(/[^0-9A-Fa-f]/g, "").slice(0, 6);
                        setHexHasHash(hasHash);
                        setHexInput(hex);
                        if (hex.length > 0) handleColorPicked("#" + hex);
                      }}
                      placeholder="#0caa41"
                      maxLength={7}
                      style={{
                        width: '120px',
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '1rem',
                        fontFamily: 'monospace',
                        textTransform: 'uppercase'
                      }}
                    />
                  </div>
                </div>
              </div>
              <div id="cardProto">
                <p
                  style={{
                    color: "var(--color-grey)",
                    fontSize: "1.2rem",
                    textAlign: "center",
                  }}
                >
                  Présentation du recto de la carte sur l'app Carte Verte.
                </p>
                <FrontCard
                  color={color}
                  oldColor={oldColor}
                  textColor={textColor}
                  oldTextColor={oldTextColor}
                  name={name}
                  oldName={oldName}
                  logo={logo}
                  oldLogo={oldLogo}
                  displayName={displayName}
                  oldDisplayName={oldDisplayName}
                />

                <p
                  style={{
                    color: "var(--color-grey)",
                    fontSize: "1.2rem",
                    textAlign: "center",
                  }}
                >
                  Présentation du verso de la carte sur l'app Carte Verte.
                </p>
                <BackCardExplore
                  color={color}
                  oldColor={oldColor}
                  textColor={textColor}
                  oldTextColor={oldTextColor}
                  offerMsgsCheckboxChecked={offerMsgsCheckboxChecked}
                  offerMsgs={offerMsgs}
                  oldOfferMsgs={oldOfferMsgs}
                  defaultOfferMsgs={defaultOfferMsgs}
                  offer={offer}
                  oldOffer={oldOffer}
                />
                {/* <div
                      
                      className="frontCard"
                      style={{
                        backgroundColor: color || oldColor || "#0caa41",
                        color: textColor || oldTextColor || "#000000",
                        position: "absolute",
                        top: "0",
                        opacity: cardInfo1 ? 1 : 0,
                        visibility: cardInfo1 ? "visible" : "hidden",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faArrowLeft}
                        onClick={() => setCardInfo1(false)}
                        style={{ fontSize: "1.4rem", marginLeft: "-1rem" }}
                      />
                      <div className="cardInfo">
                        <div className="cardInfo_address">
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            style={{
                              fontSize: "1.4rem",
                              marginRight: "1rem",
                            }}
                          />
                          <div
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {oldAddresses &&
                              oldAddresses.split(";!#").length == 1
                              ? oldAddresses.split(";!#")[0]
                              : "Voir les adresses dans ma ville"}
                          </div>
                        </div>
                        <div className="cardInfo_phone">
                          <FontAwesomeIcon
                            icon={faPhone}
                            style={{
                              fontSize: "1.4rem",
                              marginRight: "1rem",
                            }}
                          />
                          <div>{phone || oldPhone}</div>
                        </div>
                        <div className="cardInfo_offer">
                          <FontAwesomeIcon
                            icon={faGift}
                            style={{
                              fontSize: "1.4rem",
                              marginRight: "1rem",
                            }}
                          />
                          {!offerMsgsCheckboxChecked ||
                            (offerMsgs && offerMsgs.length == 6) ||
                            (!offerMsgs &&
                              oldOfferMsgs &&
                              oldOfferMsgs.length == 6) ? (
                            <div>
                              {
                                defaultOfferMsgs[
                                offer !== "0" ? offer : oldOffer
                                ]
                              }
                            </div>
                          ) : !offerMsgs ||
                            (offerMsgs && offerMsgs.length == 6) ? (
                            <div>
                              <div>{oldOfferMsgs.split(";!#")[0]}</div>
                              <div>{oldOfferMsgs.split(";!#")[1]}</div>
                              <div>{oldOfferMsgs.split(";!#")[2]}</div>
                            </div>
                          ) : (
                            <div>
                              <div>{offerMsgs.split(";!#")[0]}</div>
                              <div>{offerMsgs.split(";!#")[1]}</div>
                              <div>{offerMsgs.split(";!#")[2]}</div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div id="icons" style={{ justifyContent: "center" }}>
                        <FontAwesomeIcon
                          icon={faTimes}
                          onClick={() => setCardInfo1(false)}
                          style={{ fontSize: "1.35rem", color: "" }}
                        />
                      </div>
                    </div> */}

                <p
                  style={{
                    color: "var(--color-grey)",
                    fontSize: "1.2rem",
                    textAlign: "center",
                  }}
                >
                  Verso de la carte une fois ajoutée au portefeuille client
                  sur l'app Carte Verte.
                  <br />
                  Cliquez sur le<FontAwesomeIcon
                    icon={faInfoCircle}
                    style={{ fontSize: "1.35rem", margin: "0 .5rem" }}
                  />pour voir les infos.
                </p>
                <BackCardWallet
                  color={color}
                  oldColor={oldColor}
                  textColor={textColor}
                  oldTextColor={oldTextColor}
                  cardInfo2={cardInfo2}
                  setCardInfo2={setCardInfo2}
                  squaresRow1={squaresRow1}
                  squaresRow2={squaresRow2}
                  squaresRow3={squaresRow3}
                  name={name}
                  oldName={oldName}
                  offerMsgsCheckboxChecked={offerMsgsCheckboxChecked}
                  offerMsgs={offerMsgs}
                  oldOfferMsgs={oldOfferMsgs}
                  defaultOfferMsgs={defaultOfferMsgs}
                  offer={offer}
                  oldOffer={oldOffer}
                />
              </div>
            </div>
          </div>
        </div>

        <div id="CTAs">
          <div
            id="activateAccount"
            style={{ display: `${oldIsActive ? "none" : "block"}` }}
          >
            <div id="activateAccountCheckbox">
              <p id="activateAccountCheckboxLabel">
                <label htmlFor="status">
                  Activer mon compte
                  <br />
                </label>

                <span>
                  Rendre votre carte visible sur l'application Carte Verte.
                  <br />
                  Tous les champs doivent être renseignés pour l'activation du
                  compte.
                </span>
              </p>
              <input
                type="checkbox"
                id="status"
                name="gender"
                value={isActive}
                onChange={(event) => setIsActive(event.target.checked)}
              />
            </div>
            <p className="error activateAccountError">
              Vous ne pouvez pas activer votre compte tant que tous les champs
              ne sont pas remplis.
            </p>
            <p className="error networkError">
              Veuillez vérifier vos informations
            </p>
          </div>
          <p className="error cnxError">Vérifiez votre connexion internet !</p>
          <p className="error nochange">
            Vous n'avez effectué aunune modification !
          </p>
          <button
            className="saveAccountBtn createButton"
            onClick={() => {
              let error = false;
              let data = {};
              if (name) data.name = name;
              if (displayName !== undefined) data.displayName = displayName;
              if (email) data.email = email;
              if (password) {
                if (password.length < 8 || password !== newPassword) {
                  if (password.length < 8) {
                    editElem(".newPasswordLengthError", "display", "block");
                  } else {
                    editElem(".newPasswordMatchError", "display", "block");
                  }
                  document
                    .querySelector("#newPassword")
                    .parentElement.scrollIntoView();
                  error = true;
                } else data.password = password;
              }

              // Handle addresses from AddressManager
              if (currentAddresses.length > 0) {
                // Format: streetNb|streetName|postalCode|city|departement|country##lat|lon
                const formattedAddresses = currentAddresses.map(addr => {
                  const baseParts = [
                    addr.streetNb || '',
                    addr.streetName || '',
                    addr.postalCode || '',
                    addr.city || '',
                    addr.adminLevel2 || addr.department || '',
                    addr.country || 'France'
                  ];
                  
                  const addressString = baseParts.join('|');
                  
                  // Append lat/lon if available (with ## separator)
                  if (addr.lat && addr.lon) {
                    return addressString + '##' + addr.lat + '|' + addr.lon;
                  }
                  
                  return addressString;
                }).join(';!#');

                if (formattedAddresses !== oldAddresses) {
                  data.addresses = formattedAddresses;
                }
              } else if (currentAddresses.length === 0 && oldAddresses) {
                // All addresses were deleted
                data.addresses = '';
              }

              if (phone) {
                // if (!isPhoneValid) {
                //   editElem(".phoneNumberError", "display", "block");
                //   error = true;
                // } else {
                //   // Format phone for mutation (removes leading 0 for countries that use it)
                //   const country = getCountryByDialCode(phone);
                //   const formattedPhone = formatPhoneForMutation(country, phone);
                  data.phone = phone;
                // }
              }

              if (color) data.color = color;
              else if (!oldColor && !color) data.color = "#0caa41";

              if (textColor) data.textColor = textColor;
              else if (!oldTextColor && !textColor) data.textColor = "#000000";

              if (logo) {
                data.logo = logo;
              }
              if (commerceType && commerceType !== "0") {
                data.commerceType = commerceType;
              }
              if (offer && offer !== "0") {
                data.offer = offer;
              }
              if (offerMsgs && offerMsgs.length >= 6) {
                data.offerMsgs = offerMsgs;
              }

              if (isActive) {
                if (
                  (!oldAddresses && currentAddresses.length === 0) ||
                  (!oldPhone && !phone) ||
                  (!oldLogo && !logo) ||
                  (!oldCommerceType && !commerceType) ||
                  (!oldOffer && !offer)
                ) {
                  editElem(".activateAccountError", "display", "block");
                  error = true;
                } else {
                  data.isActive = !oldIsActive;
                  oldIsActive = !oldIsActive;
                }
              }
              if (!isActive)
                editElem(".activateAccountError", "display", "none");

              if (!error && Object.entries(data).length !== 0) {
                editElem(".saveAccountBtn", "disabled", true);
                editElem(".smallLoader2", "display", "block");
                updateOwner({
                  variables: {
                    data,
                  },
                })
                  .then((response) => {
                    handleMutationStatus("success");
                  })
                  .catch((e) => {
                    editElem(".saveAccountBtn", "disabled", false);
                    editElem(".smallLoader2", "display", "none");
                    if (
                      e
                        .toString()
                        .toLowerCase()
                        .match(/unexpected (character|token|cvtok)/)
                    ) {
                      editElem(".logoError", "display", "block");
                      document
                        .querySelector(".logoError")
                        .parentElement.scrollIntoView();
                    } else if (
                      e
                        .toString()
                        .includes("Network error: Response not successful:")
                    ) {
                      editElem(".networkError", "display", "block");
                    } // e.toString().includes("Looks like an internal server")
                    else {
                      handleMutationStatus(
                        "error",
                        "Notre serveur est déconnecté, veuillez réessayer ultérieurement !"
                      );
                    }
                  });
              } else if (!error && Object.entries(data).length == 0) {
                editElem(".nochange", "display", "block");
                setTimeout(() => {
                  editElem(".nochange", "display", "none");
                }, 3000);
              }
            }}
          >
            Sauvegarder
            <div className="smallLoader2"></div>
          </button>

          <div className="smart-popin" id="popin1">
            <div className="sp-table">
              <div className="sp-cell">
                <div className="sp-body">
                  <p>
                    <b>
                      Attention, vous êtes sur le point de supprimer votre
                      compte !
                    </b>
                  </p>
                  <p>
                    Votre commerce ne sera plus disponible sur l'application
                    Carte Verte.
                  </p>
                  <p>
                    Et tous vos clients perderont leurs cartes définitivement.
                  </p>

                  <button
                    className="deleteAccountBtn deleteAccountBtnPopin"
                    onClick={() => {
                      editElem(".deleteAccountBtnPopin", "disabled", true);

                      document.querySelector(
                        ".deleteAccountBtnPopin"
                      ).innerText = "Supperssion...";
                      deleteOwner()
                        .then(() => {
                          sessionStorage.removeItem("loggedIn");
                          Cookies.remove("subTok");
                          window.location.href = "/#";
                        })
                        .catch((e) => {
                          document.querySelector(
                            ".deleteAccountBtnPopin"
                          ).innerText = "Je confirme la suppression";
                          editElem(".deleteAccountBtnPopin", "disabled", false);
                          console.log(e.message);
                        });
                    }}
                  >
                    Je confirme la suppression
                  </button>
                </div>

                <a href="##" className="sp-back"></a>
              </div>
            </div>
          </div>

          <div className="smart-popin" id="popin2">
            <div className="sp-table">
              <div className="sp-cell">
                <div className="sp-body">
                  <p>
                    <b>
                      Attention, vous êtes sur le point de désactiver votre
                      compte !
                    </b>
                  </p>
                  <p>
                    Votre commerce ne sera plus disponible sur l'application
                    Carte Verte.
                  </p>
                  <p>
                    Vos clients garderont leurs cartes actuelles et vous pouvez
                    réactiver votre compte à tout moment.
                  </p>

                  <button
                    className="disableAccountBtn disableAccountBtnPopin"
                    onClick={() => {
                      editElem(".disableAccountBtnPopin", "disabled", true);

                      document.querySelector(
                        ".disableAccountBtnPopin"
                      ).innerText = "Désactivation...";
                      let data = {
                        isActive: false,
                      };
                      updateOwner({
                        variables: {
                          data,
                        },
                      })
                        .then((response) => {
                          handleMutationStatus("success");
                        })
                        .catch((e) => {
                          console.log("ERRORLOGO", e.toString());
                          document.querySelector(".sp-back").click();
                          editElem(
                            ".disableAccountBtnPopin",
                            "disabled",
                            false
                          );

                          document.querySelector(
                            ".disableAccountBtnPopin"
                          ).innerText = "Je confirme la désactivation";
                          if (
                            e
                              .toString()
                              .includes(
                                "Network error: Response not successful:"
                              )
                          ) {
                            editElem(".networkError", "display", "block");
                          } // e.toString().includes("Looks like an internal server")
                          else {
                            handleMutationStatus(
                              "error",
                              "Notre serveur est déconnecté, veuillez réessayer ultérieurement !"
                            );
                          }
                        });
                    }}
                  >
                    Je confirme la désactivation
                  </button>
                </div>

                <a href="##" className="sp-back"></a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ margin: "auto", width: "80%" }}>
        <Link className="returnLink" to="/mon-compte">
          <FontAwesomeIcon
            icon={faArrowLeft}
            style={{ fontSize: "1.4rem", marginRight: "1rem" }}
          />
          Mon compte
        </Link>
      </div>
      <p
        className="disableAccount"
        style={{
          display: `${oldIsActive ? "block" : "none"}`,
          marginBottom: ".6rem",
        }}
      >
        Si vous souhaitez <span style={{ fontWeight: "bold" }}>désactiver</span>{" "}
        votre compte cliquez
        <a className="disableAccountLink" href="#popin2">
          {" "}
          ici.
        </a>
      </p>
      <p className="deleteAccount">
        Si vous souhaitez <span style={{ fontWeight: "bold" }}>supprimer</span>{" "}
        votre compte cliquez
        <a className="deleteAccountLink" href="#popin1">
          {" "}
          ici.
        </a>
      </p>
      <div id="privateData">
        <p>
          Vos données sont traitées par <b>Carte Verte</b> pour permettre la
          création de votre compte, gérer et optimiser la relation clientèle,
          pour des besoins de statistiques et d’enquêtes et, si vous avez coché
          les cases correspondantes, pour vous informer des actualités et des
          offres commerciales de Carte Verte.
          <br />
          <br />
          Conformément à la Loi n°78-17 « Informatiques et Libertés » du 6
          janvier 1978, modifiée par la loi du 6 août 2004, vous bénéficiez d’un
          droit d’accès, de rectification, d’opposition, de suppression et de
          portabilité des données vous concernant. Vous pouvez à tout moment
          exercer ces droits auprès de notre Service clients à l’adresse
          suivante <b>contact@carteverte.co</b>.
          <br />
          <br />
          En cas de difficulté concernant la gestion de vos données
          personnelles, vous pouvez adresser une réclamation auprès de la CNIL
          ou de toute autre autorité compétente.
        </p>
      </div>

      <br />
    </div >
  );
};

export { EditAccountPage as default };
